import { NGROK } from "../../../APIs";
import { apiClient } from "../../../utils/apiClient";

const SECRET_DISCOVERY_BASE = `${NGROK}/api/secret-discovery`;

export type SecretDiscoveryScanState =
  | "IN_PROGRESS"
  | "COMPLETED"
  | "COMPLETED_WITH_ERRORS"
  | "FAILED"
  | "CANCELLED";

export type SecretDiscoveryTargetState =
  | "PENDING"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "FAILED"
  | "SKIPPED";

export type SecretDiscoveryTargetType =
  | "COMPUTE_VM"
  | "STORAGE_BUCKET"
  | "DATABASE"
  | "KUBERNETES_CLUSTER";

export interface SecretDiscoveryScan {
  scanId: string;
  tenantName: string;
  projectId: string;
  status: SecretDiscoveryScanState;
  running: boolean;
  initiatedBy?: string;
  startedAt?: string;
  endedAt?: string;
  durationMillis?: number;
  targetsPlanned: number;
  targetsCompleted: number;
  targetsFailed: number;
  targetsSkipped: number;
  secretsScanned: number;
  secretsSkipped: number;
  findingsCount: number;
  progressPercent: number;
  errorMessage?: string;
}

export interface SecretDiscoveryScanTarget {
  targetId: string;
  targetType: SecretDiscoveryTargetType;
  resourceId: string;
  resourceName: string;
  zone?: string;
  status: SecretDiscoveryTargetState;
  failureReason?: string;
  failureDetail?: string;
  startedAt?: string;
  endedAt?: string;
  durationMillis?: number;
  findingsCount?: number;
}

interface ApiFootprintLocation {
  findingId: string;
  resourceName: string;
  targetType: SecretDiscoveryTargetType;
  zone?: string;
  storedAs?: string;
  locationType: string;
  locationLabel?: string;
  locationPath?: string;
  lineNumber?: number;
  matchEncoding?: string;
  confidence?: number;
  contextRedacted?: string;
  status: string;
  firstSeenAt?: string;
  lastSeenAt?: string;
}

interface ApiFootprintResponse {
  secretName: string;
  secretDisplayName?: string;
  totalLocations: number;
  countsByTargetType: Record<string, number>;
  lastScannedAt?: string;
  lastScan?: SecretDiscoveryScan | null;
  locations: ApiFootprintLocation[];
}

export type SecretDiscoverySourceType =
  | "compute"
  | "storage"
  | "database"
  | "kubernetes"
  | "endpoint"
  | "github"
  | "gitlab";

export interface SecretDiscoveryLocation {
  id: string;
  sourceType: SecretDiscoverySourceType;
  sourceLabel: string;
  resourceName: string;
  storedAs: string;
  location: string;
  environment?: "Production" | "Staging" | "Development";
  region: string;
  branch?: string;
  lastSeenAt: string;

  locationType?: string;
  locationPath?: string;
  lineNumber?: number;
  confidence?: number;
  matchEncoding?: string;
  contextRedacted?: string;
  firstSeenAt?: string;
}

export interface SecretDiscoveryData {
  secretName: string;
  scanId: string | null;

  scanState: SecretDiscoveryScanState | null;

  scanStatus: string;
  scannedAt: string;
  locations: SecretDiscoveryLocation[];
}

export interface FetchSecretDiscoveryParams {
  cloudProvider: string;
  secretName: string;
  projectId?: string;
  tenantName?: string;
}

const TARGET_TYPE_TO_SOURCE: Record<
  SecretDiscoveryTargetType,
  { sourceType: SecretDiscoverySourceType; label: string }
> = {
  COMPUTE_VM: { sourceType: "compute", label: "Compute Engine VM" },
  STORAGE_BUCKET: { sourceType: "storage", label: "Cloud Storage" },
  DATABASE: { sourceType: "database", label: "Cloud SQL" },
  KUBERNETES_CLUSTER: { sourceType: "kubernetes", label: "GKE ConfigMap" },
};

export const SCAN_STATE_LABELS: Record<SecretDiscoveryScanState, string> = {
  IN_PROGRESS: "Scan in progress",
  COMPLETED: "Completed",
  COMPLETED_WITH_ERRORS: "Completed with errors",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatScanTimestamp(value?: string | null): string {
  if (!value) return "-";
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(value);
  if (!match) return value;
  const [, year, month, day, hour, minute] = match;
  const monthLabel = MONTHS[Number(month) - 1] || month;
  return `${day} ${monthLabel} ${year}, ${hour}:${minute}`;
}

export function formatDuration(millis?: number | null): string {
  if (!millis || millis < 0) return "-";
  const totalSeconds = Math.round(millis / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${seconds}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export const getShortSecretName = (value: string): string => {
  const parts = String(value || "").split("/").filter(Boolean);
  return parts[parts.length - 1] || "";
};

function isSupportedProvider(cloudProvider: string): boolean {

  return String(cloudProvider || "").toLowerCase() === "gcp";
}

function toLocation(location: ApiFootprintLocation): SecretDiscoveryLocation {
  const source = TARGET_TYPE_TO_SOURCE[location.targetType] || {
    sourceType: "compute" as SecretDiscoverySourceType,
    label: location.targetType,
  };
  return {
    id: location.findingId,
    sourceType: source.sourceType,
    sourceLabel: source.label,
    resourceName: location.resourceName,
    storedAs: location.storedAs || "-",
    location: location.locationLabel || location.locationPath || "-",
    region: location.zone || "-",
    locationType: location.locationType,
    lastSeenAt: formatScanTimestamp(location.lastSeenAt),
    locationPath: location.locationPath,
    lineNumber: location.lineNumber,
    confidence: location.confidence,
    matchEncoding: location.matchEncoding,
    contextRedacted: location.contextRedacted,
    firstSeenAt: formatScanTimestamp(location.firstSeenAt),
  };
}

export async function fetchSecretDiscovery({
  cloudProvider,
  secretName,
  projectId,
  tenantName,
}: FetchSecretDiscoveryParams): Promise<SecretDiscoveryData | null> {
  if (!isSupportedProvider(cloudProvider) || !secretName || !projectId || !tenantName) {
    return null;
  }

  const response = await apiClient<ApiFootprintResponse>({
    method: "get",
    url: `${SECRET_DISCOVERY_BASE}/gcp/footprint`,
    params: { tenantName, projectId, secretName },
  });

  if (!response || !Array.isArray(response.locations) || response.locations.length === 0) {
    return null;
  }

  const scanState = response.lastScan?.status ?? null;
  return {
    secretName: response.secretDisplayName || getShortSecretName(response.secretName),
    scanId: response.lastScan?.scanId ?? null,
    scanState,
    scanStatus: scanState ? SCAN_STATE_LABELS[scanState] : "Completed",
    scannedAt: formatScanTimestamp(response.lastScannedAt),
    locations: response.locations.map(toLocation),
  };
}

export async function fetchSecretFootprintCounts(
  cloudProvider: string,
  tenantName?: string,
  projectId?: string,
): Promise<Record<string, number>> {
  if (!isSupportedProvider(cloudProvider) || !tenantName || !projectId) {
    return {};
  }
  const counts = await apiClient<Record<string, number>>({
    method: "get",
    url: `${SECRET_DISCOVERY_BASE}/gcp/footprint-counts`,
    params: { tenantName, projectId },
  });
  return counts || {};
}

export function getSecretDiscoveryLocationCount(
  counts: Record<string, number> | undefined,
  secretName?: string,
): number {
  if (!counts || !secretName) return 0;
  if (counts[secretName] != null) return counts[secretName];
  const shortName = getShortSecretName(secretName);
  const match = Object.keys(counts).find((key) => getShortSecretName(key) === shortName);
  return match ? counts[match] : 0;
}

export class SecretDiscoveryScanConflictError extends Error {
  runningScanId?: string;

  constructor(message: string, runningScanId?: string) {
    super(message);
    this.name = "SecretDiscoveryScanConflictError";
    this.runningScanId = runningScanId;
  }
}

export async function startSecretDiscoveryScan(
  tenantName: string,
  projectId: string,
): Promise<SecretDiscoveryScan> {
  try {
    return await apiClient<SecretDiscoveryScan>({
      method: "post",
      url: `${SECRET_DISCOVERY_BASE}/gcp/scan`,
      data: { tenantName, projectId },
    });
  } catch (error: any) {

    const body = error?.response?.data ?? error;
    if (body?.code === "SCAN_IN_PROGRESS") {
      throw new SecretDiscoveryScanConflictError(
        body?.message || "A scan is already in progress for this project",
        body?.runningScanId,
      );
    }
    const message =
      (typeof body === "string" && body) ||
      body?.message ||
      error?.message ||
      "Unable to start the scan";
    throw new Error(message);
  }
}

export async function fetchLatestSecretDiscoveryScan(
  tenantName?: string,
  projectId?: string,
): Promise<SecretDiscoveryScan | null> {
  if (!tenantName || !projectId) return null;
  const scan = await apiClient<SecretDiscoveryScan | null>({
    method: "get",
    url: `${SECRET_DISCOVERY_BASE}/gcp/scan/latest`,
    params: { tenantName, projectId },
  });
  return scan || null;
}

export async function fetchSecretDiscoveryScanTargets(
  scanId?: string | null,
): Promise<SecretDiscoveryScanTarget[]> {
  if (!scanId) return [];
  const targets = await apiClient<SecretDiscoveryScanTarget[]>({
    method: "get",
    url: `${SECRET_DISCOVERY_BASE}/scans/${scanId}/targets`,
  });
  return Array.isArray(targets) ? targets : [];
}

export async function fetchSecretDiscoveryScanHistory(
  tenantName?: string,
  projectId?: string,
  limit = 10,
): Promise<SecretDiscoveryScan[]> {
  if (!tenantName || !projectId) return [];
  const scans = await apiClient<SecretDiscoveryScan[]>({
    method: "get",
    url: `${SECRET_DISCOVERY_BASE}/gcp/scans`,
    params: { tenantName, projectId, limit },
  });
  return Array.isArray(scans) ? scans : [];
}
